/**
 * Announcements 状态管理 Store
 *
 * 仅内存缓存公告列表数据，刷新页面后重新请求，避免持久化接口业务数据。
 */

import { create } from "zustand";

import { listAnnouncements, type Announcement } from "@/api";

export type AnnouncementPageKey = `${number}:${number}`;

/** Cached announcement pages are considered fresh for this long before a background revalidate. */
const ANNOUNCEMENT_STALE_TIME_MS = 5 * 60 * 1000;

export interface AnnouncementPageCache {
  announcements: Announcement[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  lastUpdated: number | null;
}

export type RefreshAnnouncementsOptions = {
  page: number;
  limit: number;
  /** 为 true 时忽略内存缓存重新请求；默认命中已加载数据时直接返回 */
  force?: boolean;
};

export interface AnnouncementsState {
  pages: Record<AnnouncementPageKey, AnnouncementPageCache>;
  refreshAnnouncements: (options: RefreshAnnouncementsOptions) => Promise<void>;
  clear: () => void;
}

function getAnnouncementPageKey(page: number, limit: number): AnnouncementPageKey {
  return `${page}:${limit}`;
}

function createEmptyPage(page: number, limit: number): AnnouncementPageCache {
  return {
    announcements: [],
    total: 0,
    page,
    limit,
    loading: false,
    loaded: false,
    error: null,
    lastUpdated: null,
  };
}

export const useAnnouncementsStore = create<AnnouncementsState>()((set, get) => ({
  pages: {},

  refreshAnnouncements: async ({ page, limit, force = false }) => {
    const key = getAnnouncementPageKey(page, limit);
    const current = get().pages[key];

    if (current?.loading) return;
    if (!force && current?.loaded) {
      const fresh =
        current.lastUpdated !== null &&
        Date.now() - current.lastUpdated < ANNOUNCEMENT_STALE_TIME_MS;
      if (fresh) return;
    }

    set((state) => ({
      pages: {
        ...state.pages,
        [key]: {
          ...(current ?? createEmptyPage(page, limit)),
          loading: true,
          error: null,
        },
      },
    }));

    try {
      const res = await listAnnouncements({ page, limit });
      if (res.ok) {
        set((state) => ({
          pages: {
            ...state.pages,
            [key]: {
              announcements: res.data.list,
              total: res.data.total,
              page: res.data.page,
              limit: res.data.limit,
              loading: false,
              loaded: true,
              error: null,
              lastUpdated: Date.now(),
            },
          },
        }));
      } else {
        set((state) => {
          const prev = state.pages[key] ?? createEmptyPage(page, limit);
          // Keep last good data on a background revalidate failure (SWR semantics);
          // only surface the error when the page never loaded successfully.
          return {
            pages: {
              ...state.pages,
              [key]: {
                ...prev,
                loading: false,
                loaded: prev.loaded,
                error: prev.loaded ? null : res.error.message,
              },
            },
          };
        });
      }
    } catch (error) {
      set((state) => {
        const prev = state.pages[key] ?? createEmptyPage(page, limit);
        return {
          pages: {
            ...state.pages,
            [key]: {
              ...prev,
              loading: false,
              loaded: prev.loaded,
              error: prev.loaded
                ? null
                : error instanceof Error
                  ? error.message
                  : "load_failed",
            },
          },
        };
      });
    }
  },

  clear: () => {
    set({ pages: {} });
  },
}));

export const selectAnnouncementPage = (
  state: AnnouncementsState,
  page: number,
  limit: number
) => state.pages[getAnnouncementPageKey(page, limit)] ?? createEmptyPage(page, limit);
